import type { ComponentType, FC } from 'react'
import { useState } from 'react'

import {
  AssetFallbackIcon,
  Card,
  DataPoint,
  DataPoints,
  ExpandableDescription,
  Popup,
  StyledText,
} from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import CheckboxMarkedCircleOutlineIcon from 'mdi-react/CheckboxMarkedCircleOutlineIcon'
import InfoIcon from 'mdi-react/InfoIcon'
import RecycleIcon from 'mdi-react/RecycleIcon'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/App.context'
import { usePolicyId } from '@/components/ecom-policy/hooks/use-policy-id'
import { useSelectProduct } from '@/components/ecom-policy/hooks/use-select-product'
import { getDifferenceInMonths } from '@/components/ecom-policy/policy/helpers'
import { ActionCardButton } from '@/components/ecom-policy/policy/policy-products/common/product-card/ActionCardButton'
import { usePolicyContext } from '@/components/ecom-policy/policy/Policy.context'
import type { GreenpoolAsset } from '@/components/ecom-policy/types'
import { Highlight } from '@/components/Highlight'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import { selectedStyleByWhiteLabel } from '@/components/util/style-constant'
import { useCart } from '@/contexts/cart/cart-context'
import { useIsThreeStepIT } from '@/hooks/user'

export const GreenpoolProduct: FC<
  GreenpoolAsset & { isOnlyProductView: boolean; searchQuery: string }
> = ({ isOnlyProductView, searchQuery, ...asset }) => {
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false)
  const policyId = usePolicyId()
  const translate = useTranslation()
  const { whiteLabel } = useAppContext()
  const isThreeStepIT = useIsThreeStepIT()
  const policyCtx = usePolicyContext()
  const { getItemsByPolicy, getItemInCart } = useCart()
  const { handleGreenpoolProductSelect } = useSelectProduct()
  const [selectedGreenpoolProductId] = policyCtx.selectedGreenpoolProductId
  const selectedItemOnAnotherPolicy = getItemInCart(asset.id)

  const isSelectedOnAnotherPolicy =
    selectedItemOnAnotherPolicy &&
    selectedItemOnAnotherPolicy.isGreenpoolProduct &&
    Number(selectedItemOnAnotherPolicy.policyId) !== policyId

  const isSelected = !isSelectedOnAnotherPolicy && Boolean(selectedGreenpoolProductId === asset.id)

  const hasSelectedProducts = getItemsByPolicy(policyId).some(
    item => item.productId && !item.isGreenpoolProduct
  )

  return (
    <li className="m-0 p-0">
      <Card
        className={twJoin(
          'shadow-basic relative !m-0 h-full w-full border-2 border-solid',
          isSelected
            ? selectedStyleByWhiteLabel[whiteLabel]?.border ||
                selectedStyleByWhiteLabel.skytech!.border
            : 'border-transparent',
          !isSelectedOnAnotherPolicy && 'hover:shadow-basic-black focus:shadow-basic-black'
        )}
      >
        <div className="flex h-full flex-col items-center">
          <div
            className={twJoin(
              'absolute right-2 top-2',
              isThreeStepIT && 'fill-threeStepIT',
              !isThreeStepIT &&
                (isSelected
                  ? selectedStyleByWhiteLabel[whiteLabel]?.fill ||
                    selectedStyleByWhiteLabel.skytech!.fill
                  : 'fill-green-badge')
            )}
          >
            {isSelected ? (
              <CheckboxMarkedCircleOutlineIcon
                size={20}
                color={twJoin(
                  selectedStyleByWhiteLabel[whiteLabel]?.fill ||
                    selectedStyleByWhiteLabel.skytech!.fill
                )}
              />
            ) : (
              <RecycleIcon
                size={20}
                color={twJoin(isThreeStepIT ? 'fill-threeStepIT' : 'fill-green-badge')}
              />
            )}
          </div>

          {/* Info icon */}
          {(asset.conditionDescription || asset.deliveryDate) && (
            <div className="absolute right-2 top-6 z-[19]">
              <Button
                isIconRight
                variant="tertiary"
                className={twJoin(
                  'm-0 h-8 p-0 pt-2',
                  isThreeStepIT ? 'fill-threeStepIT-neutral' : 'fill-sky-600'
                )}
                Icon={
                  <InfoIcon
                    size={22}
                    color={twJoin(isThreeStepIT ? 'fill-threeStepIT-neutral' : 'fill-sky-600')}
                  />
                }
                onClick={() => setIsProductsModalOpen(true)}
              />
            </div>
          )}

          <div className="flex h-full w-full flex-col items-center">
            <div className="mobile:mb-4 m-0 mb-2 flex h-28 w-full items-center justify-center overflow-hidden border-none bg-transparent p-0 transition hover:scale-125">
              <ImageWithIconFallback
                src={asset.imgUrl || ''}
                alt="product"
                className="mx-auto block h-full max-w-full transition"
                fallbackIcon={
                  <AssetFallbackIcon
                    assetType={asset.assetType || 'OTHER'}
                    size={6}
                    className="!h-full"
                  />
                }
              />
            </div>

            <div className="z-10 m-0 w-full p-0 text-left transition">
              <StyledText
                colorMode="black"
                size="regular-plus"
                fontWeight="semibold"
                className="line-clamp-3 text-ellipsis !text-xs transition sm:!text-sm md:!text-base"
              >
                <Highlight pattern={searchQuery} text={asset.assetName} />
              </StyledText>
            </div>
          </div>

          <ActionCardButton
            isSingle
            className="mt-4"
            isSelected={isSelected}
            actionDisabled={isOnlyProductView || isSelectedOnAnotherPolicy || hasSelectedProducts}
            onCardSelect={() => {
              if (isOnlyProductView || isSelectedOnAnotherPolicy || hasSelectedProducts) return
              handleGreenpoolProductSelect({ asset })
            }}
          />
        </div>

        {isSelectedOnAnotherPolicy && (
          <div className="absolute left-0 top-1/2 z-30 flex h-full w-full -translate-y-1/2 items-center justify-center rounded-lg bg-slate-50/50 text-white">
            <div
              className={twJoin(
                'flex h-fit w-full items-center justify-center text-wrap rounded-lg p-2 text-center text-sm text-white sm:text-base',
                selectedStyleByWhiteLabel[whiteLabel]?.bg || selectedStyleByWhiteLabel.skytech!.bg
              )}
            >
              <Translation id="Product already chosen" />
            </div>
          </div>
        )}
      </Card>

      {(asset.conditionDescription || asset.deliveryDate) && (
        <Popup
          variant="medium"
          isOpen={isProductsModalOpen}
          ariaLabel={translate('Modal with product description')}
          onClose={() => setIsProductsModalOpen(false)}
        >
          <GreenpoolAssetDetails
            assetName={asset.assetName}
            deliveryDate={asset.deliveryDate}
            conditionDescription={asset.conditionDescription}
          />
        </Popup>
      )}
    </li>
  )
}

const GreenpoolAssetDetails: FC<{
  assetName: string
  deliveryDate: string | null
  conditionDescription: string
}> = ({ assetName, deliveryDate, conditionDescription }) => {
  const assetAge = deliveryDate
    ? getDifferenceInMonths({ startDate: new Date(deliveryDate), endDate: new Date() })
    : null

  return (
    <DataPoints className="!m-0 !p-0">
      {/* Article name */}
      <DataPoint
        className="!mx-0 !flex-[100%] max-sm:!my-0 max-sm:!mb-2"
        value={
          <StyledText
            size="xxl"
            fontWeight="bold"
            className="!leading-none max-md:!text-xl max-sm:!text-lg"
          >
            {assetName}
          </StyledText>
        }
      />

      {/* Product manufacturer name */}
      <DataPoint
        className="!mx-0 max-sm:!my-0 max-sm:!mb-2"
        header={<Translation id="Age" />}
        value={
          <span className="!leading-none max-md:text-base max-sm:text-sm">
            {assetAge ? (
              <Translation
                id="{numberOfMonths} {numberOfMonths, plural, one {month} other {months}}"
                numberOfMonths={assetAge}
              />
            ) : (
              '-'
            )}
          </span>
        }
      />

      {/* Product description */}
      <DataPoint
        className="!mx-0 !flex-[100%] max-sm:!my-0 max-sm:!mb-2"
        header={<Translation id="Greenpool condition" className="capitalize" />}
        value={
          conditionDescription ? (
            <ExpandableDescription
              description={conditionDescription || ''}
              showFullText={false}
              TranslationComponent={Translation as ComponentType}
            />
          ) : (
            '-'
          )
        }
      />
    </DataPoints>
  )
}
